import { FormProvider, useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import DropDownPicker from "../forms/ReactHookForm/DropDown/DropDownPicker";
import {
  companySizeOptions,
  industryOptions,
} from "../../constants/constantVariables";
import {
  CheckCircle,
  Description,
  Close,
  Business,
  Group,
  Assignment,
  Star,
  ArrowForward,
} from "@material-ui/icons";
import history from "tools/history";
import request from "../../tools/request";
import Checkbox from "@material-ui/core/Checkbox";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import "./WelcomeSurvey.scss";
import { TRIAL_INCLUDED_CREDITS } from "constants/plans"

const TERMS_LIST = [
  {
    id: "t1",
    code: "ROX_PLATFORM_TOS",
    title: "Rox Platform - Terms and Conditions",
    isPdf: true,
    content: (
        <iframe
            title="Rox Platform Terms"
            className="terms-pdf"
            src="https://rox-asset.s3.us-east-2.amazonaws.com/terms/Rox_Starter_Agreement.pdf#view=FitH&navpanes=0&toolbar=0"
        />
    ),
  }
];

const availableReports = [
  {
    id: "fmcsa",
    code: "FMCSA",
    name: "FMCSA",
    description: "Federal Motor Carrier Safety Administration",
  },
  {
    id: "sos",
    code: "SOS",
    name: "SOS",
    description: "Secretary of State business filings",
  },
  {
    id: "bank-transactions",
    code: "BANKS_TRANSACTIONS",
    name: "Banks Transactions",
    description: "Bank activity and transaction analysis",
  },
  {
    id: "credit-memo",
    code: "CREDIT_MEMO",
    name: "Credit Memo",
    description: "Credit memo summary report",
  },
  {
    id: "reputational-report",
    code: "REPUTATIONAL_REPORT",
    name: "Reputational report",
    description: "Reputational risk signals and mentions",
  },
  {
    id: "ar-balance-analysis",
    code: "AR_BALANCE_ANALYSIS",
    name: "AR Balance Analysis",
    description: "Accounts receivable balance insights",
  },
];

const WelcomeSurvey = () => {
  const methods = useForm();
  const { control, watch } = methods;

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [termsAccepted, setTermsAccepted] = useState({});
  const [showTermsError, setShowTermsError] = useState(false);

  const [activeTerm, setActiveTerm] = useState(null);
  const [modalChecked, setModalChecked] = useState(false);
  const [showWelcomeVideo, setShowWelcomeVideo] = useState(false);

  const [selectedReports, setSelectedReports] = useState(
      availableReports.reduce((acc, r) => {
        acc[r.id] = true;
        return acc;
      }, {}),
  );

  const industryValue = watch("industry");
  const companySizeValue = watch("companySize");

  const allTermsAccepted = TERMS_LIST.every((t) => termsAccepted[t.id]);

  const allReportsSelected = availableReports.every(
      (r) => selectedReports[r.id],
  );
  const selectedReportsCount = Object.values(selectedReports).filter(
      Boolean,
  ).length;

  useEffect(() => {
    if (!localStorage.getItem("showWelcomeSurvey")) {
      history.push("/dashboard");
    }
  }, []);

  const toggleReport = (id) => {
    setSelectedReports((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!allTermsAccepted) {
        setShowTermsError(true);
        return;
      }
      setCurrentStep(2);
    } else {
      setLoading(true);
      request("welcome-survey", "POST", {
        industry: industryValue,
        companySize: companySizeValue,
        reports: availableReports
            .filter((r) => selectedReports[r.id])
            .map((r) => r.code),
        acceptedTerms: TERMS_LIST.filter((t) => termsAccepted[t.id]).map(
            (t) => t.code,
        ),
      })
          .then(() => {
            localStorage.removeItem("showWelcomeSurvey");
            setShowWelcomeVideo(true);
          })
          .catch(() => {
            localStorage.removeItem("showWelcomeSurvey");
            history.push("/dashboard");
          })
          .finally(() => setLoading(false));
    }
  };

  const openTermModal = (term) => {
    setActiveTerm(term);
    setModalChecked(termsAccepted[term.id] || false);
  };

  const acceptTerm = () => {
    setTermsAccepted((prev) => ({
      ...prev,
      [activeTerm.id]: true,
    }));
    setActiveTerm(null);
  };

  return (
      <FormProvider {...methods}>
        <div className="onboarding-container">
          {currentStep === 1 && (
              <>
                <div className="onboarding-header">
                  <h1>Welcome to Roxwrite!</h1>
                  <p>
                    Let's set up your workspace and get you started with
                    comprehensive business verification
                  </p>
                </div>

                <div className="onboarding-card">
                  {/* Company Section */}
                  <div className="section">
                    <h3>Tell us about your Company</h3>
                    <span>Help us customize your experience</span>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Industry*</label>
                        <DropDownPicker
                            control={control}
                            options={industryOptions}
                            name="industry"
                            labelField="label"
                            valueField="label"
                            fullWidth
                        />
                      </div>

                      <div className="form-group">
                        <label>Company Size*</label>
                        <DropDownPicker
                            control={control}
                            options={companySizeOptions}
                            name="companySize"
                            labelField="label"
                            valueField="label"
                            fullWidth
                        />
                      </div>
                    </div>
                  </div>

                  {/* Reports Section */}
                  <div className="section">
                    <h3>Configure Your Reports</h3>
                    <span>
                  All reports are pre-selected for comprehensive verification
                </span>

                    <div className="reports-grid">
                      {availableReports.map((r) => {
                        const checked = selectedReports[r.id];

                        return (
                            <div
                                key={r.id}
                                className={`report-card ${checked ? "active" : ""}`}
                                onClick={() => toggleReport(r.id)}
                            >
                              <div className="report-info">
                                <Checkbox
                                    checked={checked}
                                    onChange={() => toggleReport(r.id)}
                                    icon={<RadioButtonUncheckedIcon />}
                                    checkedIcon={<CheckCircleIcon />}
                                    className="report-checkbox"
                                />
                                <div>
                                  <strong>{r.name}</strong>
                                  <p>{r.description}</p>
                                </div>
                              </div>
                            </div>
                        );
                      })}
                    </div>

                    <div className="ucc-note">
                      <strong>*UCC Filings Configuration</strong> requires First
                      Corporate Solutions credentials. You can contact support to
                      set this up later.
                    </div>

                    {/* Terms */}
                    <div className="terms-section">
                      <h4>Accept Terms & Conditions</h4>

                      {showTermsError && !allTermsAccepted && (
                          <p className="terms-error">
                            Please read and accept the following terms to continue:
                          </p>
                      )}

                      {TERMS_LIST.map((term) => {
                        const accepted = termsAccepted[term.id];

                        return (
                            <div
                                key={term.id}
                                className={`term-item ${
                                    accepted
                                        ? "accepted"
                                        : showTermsError && !accepted
                                            ? "error"
                                            : ""
                                }`}
                                onClick={() => openTermModal(term)}
                            >
                              <span>{term.title}</span>
                              {accepted ? (
                                  <CheckCircle className="green-icon" />
                              ) : (
                                  <Description className="doc-icon" />
                              )}
                            </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="bottom-action">
                  <button
                      className="primary-btn"
                      disabled={
                          !industryValue ||
                          !companySizeValue ||
                          !allTermsAccepted ||
                          !allReportsSelected
                      }
                      onClick={handleNext}
                  >
                    Next
                  </button>
                </div>
              </>
          )}

          {currentStep === 2 && (
              <div className="completion-screen">
                <h1>You are all set!</h1>
                <p>
                  Your Roxwrite workspace is ready for comprehensive business
                  verification.
                </p>

                <div className="summary-grid">
                  <div className="summary-card">
                    <h3>Your Configuration</h3>
                    <ul className="summary-list">
                      <li>
                    <span className="item-icon" aria-hidden="true">
                      <Business className="summary-icon" />
                    </span>
                        <span>
                      Industry: <strong>{industryValue}</strong>
                    </span>
                      </li>
                      <li>
                    <span className="item-icon" aria-hidden="true">
                      <Group className="summary-icon" />
                    </span>
                        <span>
                      Company Size: <strong>{companySizeValue}</strong>
                    </span>
                      </li>
                      <li>
                    <span className="item-icon" aria-hidden="true">
                      <Assignment className="summary-icon" />
                    </span>
                        <span>
                      Active Reports: <strong>{selectedReportsCount}</strong>
                    </span>
                      </li>
                      <li>
                    <span className="item-icon" aria-hidden="true">
                      <Star className="summary-icon" />
                    </span>
                        <span>
                      Current Plan: <strong>Free Trial</strong>
                    </span>
                      </li>
                    </ul>
                  </div>

                  <div className="summary-card">
                    <h3>What's Next?</h3>
                    <ul className="next-list">
                      <li>
                        <ArrowForward className="arrow-icon" /> Access your Dashboard
                      </li>
                      <li>
                        <ArrowForward className="arrow-icon" /> Create your first
                        application
                      </li>
                      <li>
                        <ArrowForward className="arrow-icon" /> Start using your{" "}
                        {TRIAL_INCLUDED_CREDITS} trial credits
                      </li>
                      <li>
                        <ArrowForward className="arrow-icon" /> Configure UCC
                        credential in settings
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="credits-callout">
                  Your workspace is ready with 4 comprehensive verification reports.
                  You have <strong>{TRIAL_INCLUDED_CREDITS} trial credits</strong> to get started.
                  Remember, <strong>credits never expire!</strong>
                </div>

                <button
                    className="primary-btn large"
                    onClick={handleNext}
                    disabled={loading}
                >
                  Let's get started!
                </button>
              </div>
          )}

          {/* Modal */}
          {activeTerm && (
              <div className="modal-overlay">
                <div className="modal">
                  <div className="modal-header">
                    <h3>Terms & Conditions – {activeTerm.title}</h3>
                    <Close
                        className="close-icon"
                        onClick={() => setActiveTerm(null)}
                    />
                  </div>

                  <div
                      className={`modal-content ${
                          activeTerm?.isPdf ? "modal-content-pdf" : ""
                      }`}
                  >
                    {activeTerm.content}
                  </div>

                  <div className="modal-footer">
                    <div className="checkbox-row">
                      <Checkbox
                          className="terms-checkbox"
                          checked={modalChecked}
                          onChange={(e) => setModalChecked(e.target.checked)}
                      />
                      <span className="terms-label">
                    I have read and accept the Terms and conditions
                  </span>
                    </div>

                    <button
                        className="primary-btn"
                        disabled={!modalChecked}
                        onClick={acceptTerm}
                    >
                      Accept Terms & Conditions
                    </button>
                  </div>
                </div>
              </div>
          )}

          {showWelcomeVideo && (
              <div className="modal-overlay">
                <div className="video-modal">
                  <div className="modal-header">
                    <h3>Welcome to Rox! QuickStart Guide</h3>
                    <Close
                        className="close-icon"
                        onClick={() => history.push("/dashboard")}
                    />
                  </div>

                  <div className="video-frame">
                    <div className="video-frame-inner">
                      <iframe
                          src="https://guides.roxwrite.com/embed/cml0xhicl0zbzzsadn2nz4kxl?embed_v=2&utm_source=embed"
                          loading="lazy"
                          title="Welcome to Rox! QuickStart Guide"
                          allow="clipboard-write"
                          frameBorder="0"
                          allowFullScreen
                      />
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                        className="primary-btn"
                        onClick={() => history.push("/dashboard")}
                    >
                      Go to Home
                    </button>
                  </div>
                </div>
              </div>
          )}
        </div>
      </FormProvider>
  );
};

export default WelcomeSurvey;